import React, { createContext, useContext, useRef, useState, useEffect } from 'react';

import { VIDEO_STATE } from '../constant';

const PLAYER_STATE = {
  INITIALIZE: 'INITIALIZE',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

const MindstampVideoPlayerContext = createContext();

/** @param {VideoEvent} event */
function mapEventToVideoState(event) {
  switch (event) {
    case 'video_play':
      return VIDEO_STATE.PLAY;
    case 'video_pause':
      return VIDEO_STATE.PAUSED;
    case 'video_end':
      return VIDEO_STATE.ENDED;
    default:
      return null;
  }
}

function MindstampVideoPlayerProvider({ children }) {
  const [playerState, setPlayerState] = useState(PLAYER_STATE.INITIALIZE);
  const [videoState, setVideoState] = useState(VIDEO_STATE.LOAD);
  const currentTimeRef = useRef(null);
  const playerRef = useRef(null);

  const setCurrentTime = (time) => {
    currentTimeRef.current = time;
  };

  /** @param {MessageEvent<MindstampData>} messageEvent */
  const handleVideoEvent = (messageEvent) => {
    const messageData = messageEvent.data;
    if (messageData.info?.currentTime) {
      setCurrentTime(messageEvent.data.info.currentTime);
    }

    const videoState = mapEventToVideoState(messageData.event);
    if (videoState) {
      setVideoState(videoState);
    }
  };

  const play = () => {
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage({ event: 'play_player' }, '*');
    }
  };
  const pause = () => {
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage({ event: 'pause_player' }, '*');
    }
  };
  const capture = async () => {
    const controller = new AbortController();
    const signal = controller.signal;
    const getScreenshotFromMindstamp = new Promise((res) => {
      window.addEventListener(
        'message',
        (messageEvent) => {
          const event = messageEvent.data.event;
          if (event === 'screenshot') {
            res(messageEvent.data.info.image);
          }
        },
        { signal }
      );
    });

    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage({ event: 'request_screenshot' }, '*');
    }

    return await getScreenshotFromMindstamp.then((res) => {
      controller.abort();
      return res;
    });
  };

  const reset = () => {
    setCurrentTime(null);
    setVideoState(VIDEO_STATE.LOAD);
  };

  useEffect(() => {
    if (playerState === PLAYER_STATE.ACTIVE) {
      window.addEventListener('message', handleVideoEvent);
    } else if (playerState === PLAYER_STATE.INACTIVE) {
      window.removeEventListener('message', handleVideoEvent);
      reset();
    }

    return () => {
      window.removeEventListener('message', handleVideoEvent);
    };
  }, [playerState]);

  return (
    <MindstampVideoPlayerContext.Provider
      value={{
        activate: () => setPlayerState(PLAYER_STATE.ACTIVE),
        deactivate: () => setPlayerState(PLAYER_STATE.INACTIVE),
        currentTimeRef,
        playerState,
        videoState,
        playerRef,
        play,
        pause,
        capture,
      }}
    >
      {children}
    </MindstampVideoPlayerContext.Provider>
  );
}

function useMindstampVideoPlayer() {
  const context = useContext(MindstampVideoPlayerContext);
  if (context === undefined) {
    throw new Error('useMindstampVideoPlayer must be used within MindstampVideoPlayerProvider');
  }
  return context;
}

export { useMindstampVideoPlayer, PLAYER_STATE };
export default MindstampVideoPlayerProvider;

/**
 * @typedef {"video_play"|"video_pause"|"video_end"|"time_update"|"progress_90"} VideoEvent
 *
 * @typedef MindstampData reference: https://mindstamp.readme.io/reference/players-lifecycle-events
 * @property {VideoEvent} event
 * @property {object} info
 * @property {string} info.token
 * @property {number} info.currentTime
 * @property {object=} info.data
 */
